import { Fragment, useEffect, useReducer, useState } from "react";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import { TransformRule } from "../../models/transformation/TransformRule";
import { TransformRuleTester } from "../../services/transformation/TransformRuleTester";
import { Button } from "../buttons/Button";
import { useAsync } from "../hooks/useAsync";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTTransformationTestRule } from "../tooltips/ConfigurationTooltips";

export { Footer };

// copy dlfVariable stuff into Footer, see how it goes
type TransformVariableState = { [key in string]: string };
type Action = {
  type: "edit" | "remove";
  field: string;
  value: string;
};
function transformVariableReducer(
  state: TransformVariableState,
  action: Action,
): TransformVariableState {
  const newState = { ...state };
  if (action.type === "remove") {
    delete newState[action.field];
  } else {
    newState[action.field] = action.value;
  }
  return newState;
}

type Props = {
  /** Label for configurable transform rules  */
  mainListTitle: string;
  testValue?: string;
  acceleratorDocUuid?: string;
  rules: TransformRule[];
  effectiveRules?: TransformRule[];
  docFields: string[];
  /**
   * Provide a list of non editable variables.
   */
  nonEditableVariables?: string[];
  /**
   * List of variables to support. This should be removed, and should take in an enum value
   */
  variables: string[];
};

const Footer = (props: Props) => {
  const nonEditableVariables = (props.nonEditableVariables ?? []).concat(
    "%%UUID",
    "%%COUNTER",
  );
  const [testValue, setTestValue] = useState<string>(props.testValue ?? "");

  const [dlfVariables, dispatchDlfVariables] = useReducer(
    transformVariableReducer,
    {},
  );

  const [variables, dispatchVariables] = useReducer(
    transformVariableReducer,
    {},
  );

  // TODO: result should be a class with method getRuleResult(ruleId: string) : Promise<String>;
  const [result, trigger, loading] = useAsync<undefined | Array<string>>(
    // test TransformRules effect on testValue
    async () => {
      const editableVariables = Object.keys(variables).filter(
        (key) => !nonEditableVariables.includes(key),
      );
      const tester = new TransformRuleTester(
        undefined,
        props.rules,
        dlfVariables,
        // get variable keys that aren't editable
        editableVariables.reduce((prev, curr) => {
          if (nonEditableVariables.includes(curr)) {
            if (curr == "%%COUNTER") {
              prev[curr] = "0";
            }
          } else {
            prev[curr] = variables[curr] as string;
          }
          return prev;
        }, {} as TransformVariableState),
      );
      return tester.transform(testValue);
    },
    undefined,
  );
  const [effectiveResult, effectiveTrigger, effectiveLoading] = useAsync<
    undefined | Array<string>
  >(
    // test TransformRules effect on testValue
    async () => {
      const editableVariables = Object.keys(variables).filter(
        (key) => !nonEditableVariables.includes(key),
      );
      const tester = new TransformRuleTester(
        undefined,
        props.effectiveRules ?? [],
        dlfVariables,
        // get variable keys that aren't editable
        editableVariables.reduce((prev, curr) => {
          if (nonEditableVariables.includes(curr)) {
            if (curr == "%%COUNTER") {
              prev[curr] = "0";
            }
          } else {
            prev[curr] = variables[curr] as string;
          }
          return prev;
        }, {} as TransformVariableState),
      );
      return tester.transform(testValue);
    },
    undefined,
  );

  // update dlfVariables and transform variables
  useEffect(() => {
    // remove DLF prefix+suffix
    const ruleDlfVariables = props.rules
      .flatMap((x) => x.getDlfVariables())
      .concat((props.effectiveRules ?? []).flatMap((x) => x.getDlfVariables()))
      .map((variable) => variable.split(TransformRule.DLF_VARIABLE_PREFIX)[1])
      .map((variable) => variable.split(TransformRule.DLF_VARIABLE_SUFFIX)[0]);

    // filter out non editable DLF variables.
    const newRuleDlfVars = ruleDlfVariables.filter(
      (variable) => dlfVariables[variable] == undefined,
    );
    newRuleDlfVars.forEach((variable) =>
      dispatchDlfVariables({
        field: variable,
        type: "edit",
        value: "",
      }),
    );
    // leave variable prefix, filter out non editable variables
    const editableRuleVars = props.rules
      .flatMap((x) => x.getVariables())
      .concat((props.effectiveRules ?? []).flatMap((x) => x.getVariables()))
      .filter((variable) => !nonEditableVariables.includes(variable));

    const newRuleVars = editableRuleVars.filter(
      (variable) => variables[variable] == undefined,
    );
    newRuleVars.forEach((variable) =>
      dispatchVariables({
        field: variable,
        type: "edit",
        value: "",
      }),
    );

    // remove extra rules
    const removeDlfVars = Object.keys(dlfVariables).filter(
      (k) => !ruleDlfVariables.includes(k),
    );
    const removeVars = Object.keys(variables).filter(
      (k) => !editableRuleVars.includes(k),
    );
    removeDlfVars.forEach((x) =>
      dispatchDlfVariables({
        field: x,
        type: "remove",
        value: "",
      }),
    );
    removeVars.forEach((x) =>
      dispatchVariables({
        field: x,
        type: "remove",
        value: "",
      }),
    );
  }, [props.rules, props.effectiveRules]);

  return (
    // FIXME: use proper css classes, target in css correctly
    <div
      data-test-values /*style={!props.open ? { display: 'none', } : undefined}*/
    >
      <span className={"fluffer"} />
      {/* Replace Dlf variables */}
      <Variables
        type={"dlf"}
        variables={dlfVariables}
        updateValue={dispatchDlfVariables}
      />
      {/* Replace variables */}
      <Variables
        type={"var"}
        variables={variables}
        updateValue={dispatchVariables}
      />
      {/* Use onSubmit to set a single event handler for rules */}
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // do stuff
        }}
      >
        {/* Test Value input */}
        <input
          type={"text"}
          placeholder={"Example Value to Transform"}
          value={testValue}
          onChange={(e) => setTestValue(e.target.value)}
        />
        <AdvancedTooltip overflow tooltipText={TTTransformationTestRule}>
          {(tProps) => (
            <Button
              {...tProps}
              onClick={() => {
                trigger();
                effectiveTrigger();
              }}
            >
              Test Rules
            </Button>
          )}
        </AdvancedTooltip>
      </form>
      {result && result.length > 0 && (
        <div data-results>
          <p>
            {loading
              ? "Transforming Values..."
              : `${props.mainListTitle} Steps`}
          </p>
          {!loading &&
            result &&
            result.map((x, i) => (
              <span
                key={x + i}
                style={{
                  // fontStyle: 'italic',
                  lineBreak: "anywhere",
                }}
              >
                {x}{" "}
              </span>
            ))}
        </div>
      )}
      {effectiveResult && effectiveResult.length > 0 && (
        <div data-results>
          <p>
            {effectiveLoading
              ? "Transforming Values..."
              : "Document Field Transform Steps"}
          </p>
          {!effectiveLoading &&
            effectiveResult &&
            effectiveResult.map((x, i) => (
              <span
                key={x + i}
                style={{
                  // fontStyle: 'italic',
                  lineBreak: "anywhere",
                }}
              >
                {x}{" "}
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

type VariablesProps = {
  type: "dlf" | "var";
  /**
   * Variable dispatch action
   */
  updateValue: (value: Action) => void;
  /**
   * List of variables to support. This should be removed, and should take in an enum value
   */
  variables: TransformVariableState;
};

const Variables = (props: VariablesProps) => {
  {
    /* Replace variables */
  }
  const keys = Object.keys(props.variables);
  if (keys.length === 0) {
    return null;
  }
  return (
    // TODO: support changing this from type
    <div data-dlf-variables>
      {/* filter distinct */}
      {keys
        .filter((x, i, arr) => arr.indexOf(x) === i)
        // filter out variables that don't have a value because we've filtered put non editable variables
        .filter((x) => props.variables[x] != undefined)
        // commented out this line, non editable should already be filtered.
        // .filter(x => props.nonEditableVariables ? !props.nonEditableVariables.includes(x) : true)
        .map((x) => (
          <Fragment key={x}>
            <label htmlFor={x}>
              <strong>{props.type === "dlf" && "Dlf "}Variable:</strong>
              {props.type === "dlf" && x}
              {props.type === "var" && x.substring(2)}
            </label>
            <input
              key={x}
              name={x}
              value={props.variables[x] ?? ""}
              placeholder={`Example Value`}
              onChange={(e) =>
                props.updateValue({
                  type: "edit",
                  field: x,
                  value: e.target.value,
                })
              }
            />
          </Fragment>
        ))}
    </div>
  );
};
